import type { GridColDef } from '@mui/x-data-grid-pro';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Button, Stack } from '@mui/material';
import { useMemo } from 'react';

import { WelcomeBonusCampaign } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import { useModal } from '../../contexts/ModalContext';
import { createEditColumn } from '../../helpers/columns';
import { defaultColumnsWelcomeBonus } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn, formatSnakeCaseString } from '../../utils';
import MarketExclusionsForm from '../organisms/forms/welcome-bonus/market-exclusions/MarketExclusionsForm';
import WageringConfigurationForm from '../organisms/forms/WageringConfigurationForm';
import TableTemplate from '../templates/TableTemplate';

const WelcomeBonusPage = () => {
  const { openModal } = useModal();
  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<WelcomeBonusCampaign>(
    API_ROUTES.WELCOME_BONUS,
    {
      page: 1,
      limit: 25,
    },
    undefined,
    'crm'
  );

  const { deleteData } = useMutateData(API_ROUTES.WELCOME_BONUS, [QUERY_KEYS.welcomeBonus], 'crm');
  const { handleSort } = useSort(changeQuery);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const openGlobalMarketTypeExclusions = () => {
    openModal(<MarketExclusionsForm />, undefined, { dialogSize: 'extraWide' });
  };

  const openGlobalWageringSettings = () => {
    openModal(<WageringConfigurationForm />, undefined, { dialogSize: 'wide' });
  };

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('id', 'ID', { sortable: false }),
      createColumn('name', 'Name', { alwaysVisible: true, minWidth: 180 }),
      createColumn('product', 'Product', {
        alwaysVisible: true,
        valueFormatter: (value) => (value ? formatSnakeCaseString(value) : ''),
      }),
      createColumn('welcomeBonusScope', 'Scope', {
        alwaysVisible: true,
        sortable: false,
        valueFormatter: (value) => (value ? formatSnakeCaseString(value) : ''),
      }),
      createColumn('status', 'Status', {
        alwaysVisible: true,
        valueFormatter: (value) => (value ? formatSnakeCaseString(value) : ''),
      }),
      createColumn('description', 'Description', { sortable: false }),
      createEditColumn<WelcomeBonusCampaign>({
        deleteData,
        columnName: 'Actions',
      }),
    ],
    [deleteData]
  );

  return (
    <TableTemplate
      rows={data?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={data?.count || 0}
      createItemBtnLabel='Add new bonus'
      defaultVisibleColumns={defaultColumnsWelcomeBonus}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      changeQuery={changeQuery}
      pageSizeOptions={[25, 50, 100, 200, 500]}
      formType='welcome'
      customTableHeaderContent={
        <Stack direction='row' spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Button variant='outlined' startIcon={<SettingsOutlinedIcon />} onClick={openGlobalWageringSettings}>
            Global wagering settings
          </Button>
          <Button variant='outlined' startIcon={<SettingsOutlinedIcon />} onClick={openGlobalMarketTypeExclusions}>
            Global market exclusions
          </Button>
        </Stack>
      }
    />
  );
};

export default WelcomeBonusPage;
